import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';
import DocumentUpload from './DocumentUpload';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';

const PlacementsEdit = () => {
  const [placementsData, setPlacementsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',
    coordinatorName: '',
    coordinatorTitle: '',
    coordinatorImage: '',
    coordinatorMessage: '',
    careerGuidanceTitle: '',
    careerGuidanceDescription: '',
    careerPrograms: [],
    recruitersTitle: '',
    recruitersDescription: '',
    recruitersList: [],
    placementRate: '',
    partnerCompanies: '',
    highestPackage: '',
    studentEmail: '',
    studentPhone: '',
    studentTimings: '',
    corporateEmail: '',
    corporatePhone: '',
    companyBrochureUrl: ''
  });

  useEffect(() => {
    fetchPlacementsData();
  }, []);

  const fetchPlacementsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPlacements();
      const data = response.data;
      
      setPlacementsData(data);
      setFormData({
        bannerTitle: data.bannerTitle || '',
        bannerSubtitle: data.bannerSubtitle || '',
        bannerImage: data.bannerImage || '',
        coordinatorName: data.coordinatorName || '',
        coordinatorTitle: data.coordinatorTitle || '',
        coordinatorImage: data.coordinatorImage || '',
        coordinatorMessage: data.coordinatorMessage || '',
        careerGuidanceTitle: data.careerGuidanceTitle || '',
        careerGuidanceDescription: data.careerGuidanceDescription || '',
        careerPrograms: data.careerPrograms || [],
        recruitersTitle: data.recruitersTitle || '',
        recruitersDescription: data.recruitersDescription || '',
        recruitersList: data.recruitersList || [],
        placementRate: data.placementRate || '',
        partnerCompanies: data.partnerCompanies || '',
        highestPackage: data.highestPackage || '',
        studentEmail: data.studentEmail || '',
        studentPhone: data.studentPhone || '',
        studentTimings: data.studentTimings || '',
        corporateEmail: data.corporateEmail || '',
        corporatePhone: data.corporatePhone || '',
        companyBrochureUrl: data.companyBrochureUrl || ''
      });
    } catch (error) {
      setError('Failed to load placements data');
      console.error('Error fetching placements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCareerProgramChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      careerPrograms: prev.careerPrograms.map((program, i) => 
        i === index ? { ...program, [field]: value } : program
      )
    }));
  };

  const handleCareerProgramItemChange = (programIndex, itemIndex, value) => {
    setFormData(prev => ({
      ...prev,
      careerPrograms: prev.careerPrograms.map((program, i) => 
        i === programIndex ? {
          ...program,
          items: program.items.map((item, j) => j === itemIndex ? value : item)
        } : program
      )
    }));
  };

  const addCareerProgram = () => {
    setFormData(prev => ({
      ...prev,
      careerPrograms: [...prev.careerPrograms, {
        title: '',
        icon: 'fas fa-circle',
        color: 'bg-crimson',
        items: ['']
      }]
    }));
  };

  const removeCareerProgram = (index) => {
    setFormData(prev => ({
      ...prev,
      careerPrograms: prev.careerPrograms.filter((_, i) => i !== index)
    }));
  };

  const addCareerProgramItem = (programIndex) => {
    setFormData(prev => ({
      ...prev,
      careerPrograms: prev.careerPrograms.map((program, i) => 
        i === programIndex ? {
          ...program,
          items: [...program.items, '']
        } : program
      )
    }));
  };

  const removeCareerProgramItem = (programIndex, itemIndex) => {
    setFormData(prev => ({
      ...prev,
      careerPrograms: prev.careerPrograms.map((program, i) => 
        i === programIndex ? {
          ...program,
          items: program.items.filter((_, j) => j !== itemIndex)
        } : program
      )
    }));
  };

  const handleRecruiterChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      recruitersList: prev.recruitersList.map((recruiter, i) => 
        i === index ? { ...recruiter, [field]: value } : recruiter
      )
    }));
  };

  const addRecruiter = () => {
    setFormData(prev => ({
      ...prev,
      recruitersList: [...prev.recruitersList, { name: '', logoImage: '' }]
    }));
  };

  const removeRecruiter = (index) => {
    setFormData(prev => ({
      ...prev,
      recruitersList: prev.recruitersList.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (placementsData?.id) {
        await apiService.updatePlacements(placementsData.id, formData);
        setSuccess('Placements page updated successfully!');
      } else {
        await apiService.createPlacements(formData);
        setSuccess('Placements page created successfully!');
      }
      
      await fetchPlacementsData();
    } catch (error) {
      setError('Failed to save placements data');
      console.error('Error saving placements:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading placements data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">
          Placements Page Management
        </h1>
        <p className="text-gray-600">
          Manage the content for the Training & Placements page including banner, coordinator details, career programs, and recruiters.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Banner Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Banner Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Title</label>
              <input
                type="text"
                value={formData.bannerTitle}
                onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Training & Placements"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Subtitle</label>
              <textarea
                value={formData.bannerSubtitle}
                onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={2}
                placeholder="Career Development | Industry Partnerships | Professional Growth"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Image</label>
              <ImageUpload
                value={formData.bannerImage}
                onChange={(value) => handleInputChange('bannerImage', value)}
                placeholder="Banner background image filename"
              />
            </div>
          </div>
        </div>

        {/* Placement Coordinator Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Placement Coordinator</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Coordinator Name</label>
                <input
                  type="text"
                  value={formData.coordinatorName}
                  onChange={(e) => handleInputChange('coordinatorName', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Dr. [Name]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Coordinator Title</label>
                <input
                  type="text"
                  value={formData.coordinatorTitle}
                  onChange={(e) => handleInputChange('coordinatorTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Placement Coordinator"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Coordinator Image</label>
              <ImageUpload
                value={formData.coordinatorImage}
                onChange={(value) => handleInputChange('coordinatorImage', value)}
                placeholder="Coordinator photo filename (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Coordinator Message</label>
              <EnhancedRichTextEditor
                value={formData.coordinatorMessage}
                onChange={(value) => handleInputChange('coordinatorMessage', value)}
                placeholder="Message from the placement coordinator..."
              />
            </div>
          </div>
        </div>

        {/* Career Guidance Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Career Guidance Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
                <input
                  type="text"
                  value={formData.careerGuidanceTitle}
                  onChange={(e) => handleInputChange('careerGuidanceTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Career Guidance & Capacity Building"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Section Description</label>
                <input
                  type="text"
                  value={formData.careerGuidanceDescription}
                  onChange={(e) => handleInputChange('careerGuidanceDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Comprehensive programs designed to enhance employability..."
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">Career Programs</label>
                <button
                  type="button"
                  onClick={addCareerProgram}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Program
                </button>
              </div>
              
              <div className="space-y-4">
                {formData.careerPrograms.map((program, programIndex) => (
                  <div key={programIndex} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeCareerProgram(programIndex)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input
                          type="text"
                          value={program.title}
                          onChange={(e) => handleCareerProgramChange(programIndex, 'title', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Program Title"
                        />
                        <input
                          type="text"
                          value={program.icon}
                          onChange={(e) => handleCareerProgramChange(programIndex, 'icon', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Icon class (e.g., fas fa-brain)"
                        />
                        <select
                          value={program.color}
                          onChange={(e) => handleCareerProgramChange(programIndex, 'color', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                        >
                          <option value="bg-crimson">Crimson</option>
                          <option value="bg-gold">Gold</option>
                          <option value="bg-slate">Slate</option>
                          <option value="bg-blue-500">Blue</option>
                          <option value="bg-green-500">Green</option>
                        </select>
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm font-medium text-slate">Program Items</label>
                          <button
                            type="button"
                            onClick={() => addCareerProgramItem(programIndex)}
                            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
                          >
                            <i className="fas fa-plus mr-1"></i>Add Item
                          </button>
                        </div>
                        
                        <div className="space-y-2">
                          {program.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex gap-2">
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleCareerProgramItemChange(programIndex, itemIndex, e.target.value)}
                                className="flex-1 px-3 py-2 border border-border_gray rounded text-sm"
                                placeholder="Program item"
                              />
                              <button
                                type="button"
                                onClick={() => removeCareerProgramItem(programIndex, itemIndex)}
                                className="px-2 py-2 text-red-500 hover:text-red-700"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recruiters Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Recruiters Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Recruiters Title</label>
                <input
                  type="text"
                  value={formData.recruitersTitle}
                  onChange={(e) => handleInputChange('recruitersTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Our Recruiters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Recruiters Description</label>
                <input
                  type="text"
                  value={formData.recruitersDescription}
                  onChange={(e) => handleInputChange('recruitersDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Leading organizations that regularly recruit from our campus."
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">Recruiters List</label>
                <button
                  type="button"
                  onClick={addRecruiter}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Recruiter
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.recruitersList.map((recruiter, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeRecruiter(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    
                    <div className="space-y-3 pr-6">
                      <input
                        type="text"
                        value={recruiter.name}
                        onChange={(e) => handleRecruiterChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Company Name"
                      />
                      <div>
                        <label className="block text-xs font-medium text-slate mb-2">Company Logo Image</label>
                        <ImageUpload
                          value={recruiter.logoImage || ''}
                          onChange={(imageUrl) => handleRecruiterChange(index, 'logoImage', imageUrl)}
                          placeholder="Upload company logo image"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Placement Statistics */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Placement Statistics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Placement Rate</label>
              <input
                type="text"
                value={formData.placementRate}
                onChange={(e) => handleInputChange('placementRate', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="90%+"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Partner Companies</label>
              <input
                type="text"
                value={formData.partnerCompanies}
                onChange={(e) => handleInputChange('partnerCompanies', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="150+"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Highest Package</label>
              <input
                type="text"
                value={formData.highestPackage}
                onChange={(e) => handleInputChange('highestPackage', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="₹8L+"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Contact Information</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate mb-3">For Students</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.studentEmail}
                    onChange={(e) => handleInputChange('studentEmail', e.target.value)}
                    className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="placements@fit.ac.in"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">Phone</label>
                  <input
                    type="text"
                    value={formData.studentPhone}
                    onChange={(e) => handleInputChange('studentPhone', e.target.value)}
                    className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="+91 98765-43210"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">Timings</label>
                  <input
                    type="text"
                    value={formData.studentTimings}
                    onChange={(e) => handleInputChange('studentTimings', e.target.value)}
                    className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="Monday - Friday, 9:00 AM - 6:00 PM"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate mb-3">For Recruiters</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.corporateEmail}
                    onChange={(e) => handleInputChange('corporateEmail', e.target.value)}
                    className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="corporate@fit.ac.in"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">Phone</label>
                  <input
                    type="text"
                    value={formData.corporatePhone}
                    onChange={(e) => handleInputChange('corporatePhone', e.target.value)}
                    className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="+91 98765-43211"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">Company Brochure</label>
                  <DocumentUpload
                    value={formData.companyBrochureUrl}
                    onChange={(value) => handleInputChange('companyBrochureUrl', value)}
                    placeholder="Upload company brochure"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Placements Content
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlacementsEdit;